/**
 * Focus return for the controlled overlays (./dialog.jsx, ./alert-dialog.jsx,
 * ./sheet.jsx).
 *
 * On close Radix focuses its own Trigger, and nothing else: an overlay opened
 * with `open` from a menu item, a switch, a link or a row button has no
 * Trigger, so focus fell to <body>. `ReturnFocusRoot` wraps the Radix Root and
 * records the element that held focus on the render that opens it;
 * `useReturnFocus` gives the Content an `onCloseAutoFocus` that focuses that
 * element again. With nothing recorded (an uncontrolled overlay, opened by its
 * Trigger) or the element gone from the page, Radix's own handling runs.
 *
 * An opener inside a Radix menu is a menu item, which unmounts with its menu,
 * so the menu's trigger is recorded in its place.
 */
import React from 'react'

const ReturnFocusContext = React.createContext(null)

function currentOpener() {
  const active = document.activeElement
  if (!active || active === document.body) return null
  const menu = active.closest('[role="menu"]')
  const trigger =
    menu?.id &&
    document.querySelector(`[aria-controls="${CSS.escape(menu.id)}"]`)
  return trigger || active
}

function ReturnFocusRoot({ root: Root, open, ...props }) {
  const openerRef = React.useRef(null)
  const wasOpen = React.useRef(false)
  // Read during render, on the render that opens: by the time an effect runs,
  // an `autoFocus` field inside the content already holds focus.
  if (open && !wasOpen.current) openerRef.current = currentOpener()
  wasOpen.current = Boolean(open)
  return (
    <ReturnFocusContext.Provider value={openerRef}>
      <Root
        open={open}
        {...props}
      />
    </ReturnFocusContext.Provider>
  )
}

function useReturnFocus(onCloseAutoFocus) {
  const openerRef = React.useContext(ReturnFocusContext)
  return event => {
    onCloseAutoFocus?.(event)
    if (event.defaultPrevented) return
    const opener = openerRef?.current
    if (!opener || !opener.isConnected) return
    event.preventDefault()
    opener.focus({ preventScroll: true })
  }
}

export { ReturnFocusRoot, useReturnFocus }
